import React, { useState, useEffect } from 'react';
import { heroTexts, heroTools } from '../data/mock';
import { ArrowRight, Sparkles, Star, MapPin, CheckCircle, Tent, Wrench, Leaf, Home, Search, Hammer } from 'lucide-react';
import { Link } from 'react-router-dom';
import SearchBar from './SearchBar';
import { useGoogleReviews } from '../seo/googleReviews';

// Modification #5: Nouvel ordre des univers - 1. Bricolage, 2. Jardinage, 3. Nettoyage, 4. Événementiel
// Modification #4 CORRIGÉ: BRICO->"Trouvez le bon outil", JARDIN->"Découvrir les produits", NETTOYAGE->"Trouvez votre bonheur", EVENEMENT->"Voir le catalogue"
const universes = [
  { 
    name: "Bricolage - Travaux", 
    icon: Wrench, 
    color: "from-green-500 to-green-600",
    link: "/univers/bricolage-travaux",
    cta: "Trouvez le bon outil"
  },
  { 
    name: "Jardinage - Motoculture", 
    icon: Leaf, 
    color: "from-green-500 to-green-600",
    link: "/univers/jardinage-motoculture",
    cta: "Découvrir les produits"
  },
  { 
    name: "Nettoyage - Logement", 
    icon: Home, 
    color: "from-green-500 to-green-600",
    link: "/univers/nettoyage-logement",
    cta: "Trouvez votre bonheur"
  },
  { 
    name: "Evenementiel - Barnum", 
    icon: Tent, 
    color: "from-green-500 to-green-600",
    link: "/univers/evenementiel-barnum",
    cta: "Voir le catalogue"
  },
];

const Hero = () => {
  const googleReviews = useGoogleReviews();
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [currentToolIndex, setCurrentToolIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  useEffect(() => {
    const textInterval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentTextIndex((prev) => (prev + 1) % heroTexts.length);
        setIsAnimating(false);
      }, 500);
    }, 4000);

    return () => clearInterval(textInterval);
  }, []);

  useEffect(() => {
    const toolInterval = setInterval(() => {
      setCurrentToolIndex((prev) => (prev + 1) % heroTools.length);
    }, 2000);

    return () => clearInterval(toolInterval);
  }, []);

  return (
    <section className="relative min-h-[90vh] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      {/* Animated gradient orbs */}
      <div className="absolute top-20 left-10 w-96 h-96 bg-green-500/20 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />

      <div className="relative max-w-7xl mx-auto px-4 py-20 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left content */}
          <div className="text-white">
            {/* Grande note Google - MISE EN AVANT */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 leading-tight">
              <span className="block text-white">LOCATION DE</span>
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-green-500">MATÉRIEL</span>
              <span className="block text-2xl sm:text-3xl font-bold text-slate-300 mt-2">à côté du Havre et en Normandie</span>
            </h1>

            {/* Animated text */}
            <div className="h-20 mb-8 overflow-hidden">
              <p 
                className={`text-xl sm:text-2xl text-slate-300 transition-all duration-500 ${
                  isAnimating ? 'opacity-0 -translate-y-4' : 'opacity-100 translate-y-0'
                }`}
              >
                {heroTexts[currentTextIndex]}
              </p>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap items-center gap-4 mb-8">
              <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg">
                <CheckCircle size={18} className="text-green-400" />
                <span className="text-sm font-medium">+300 références</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg">
                <MapPin size={18} className="text-green-400" />
                <span className="text-sm font-medium">15min du Havre et d'Étretat</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg">
                <Sparkles size={18} className="text-green-400" />
                <span className="text-sm font-medium">7J/7 sur RDV</span>
              </div>
            </div>

            {/* Animated tool badge */}
            <div className="flex items-center gap-4 mb-10">
              <div className="flex items-center gap-3 px-6 py-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                <span className="text-white font-medium transition-all duration-500">
                  {heroTools[currentToolIndex]}
                </span>
              </div>
              <span className="text-slate-400">disponible maintenant</span>
            </div>

            {/* CTA buttons - Modification #0: Bouton "Réserver en 2min, ici" */}
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/univers"
                className="group inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-xl hover:shadow-xl hover:shadow-green-500/30 transform hover:-translate-y-1 transition-all"
              >
                Découvrir le catalogue
                <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
              </Link>
              <a 
                href="https://3dloc.lokki.rent/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl hover:shadow-xl hover:shadow-orange-500/30 transform hover:-translate-y-1 transition-all"
              >
                Réserver en 2min, ici
                <ArrowRight size={20} />
              </a>
            </div>
          </div>

          {/* Right content - Modification #1: Barre de recherche AVANT les univers */}
          <div className="flex flex-col">
            {/* Modification #0: Search Bar - taille normale */}
            <button
              onClick={() => setIsSearchOpen(true)}
              className="mb-6 w-full flex items-center gap-3 px-5 py-3 bg-white/10 backdrop-blur-sm border border-white/30 rounded-xl text-slate-300 hover:bg-white/15 hover:border-white/40 transition-all cursor-text shadow-lg"
              data-testid="hero-search-btn"
            >
              <Search size={20} className="text-green-400" />
              <span className="font-medium text-base">Rechercher un équipement...</span>
            </button>

            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-white">Nos univers</h2>
              <a 
                href="https://www.google.com/search?q=3dloc+avis" 
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 border border-white/30 rounded-full hover:border-white/50 hover:bg-white/5 transition-all"
                data-testid="google-rating-hero"
              >
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} size={14} className="fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                {googleReviews?.rating ? (
                  <>
                    <span className="text-white font-semibold text-sm">{Number(googleReviews.rating).toFixed(1)}/5</span>
                    <span className="text-slate-300 text-sm">sur Google</span>
                  </>
                ) : (
                  <span className="text-slate-300 text-sm">Avis Google</span>
                )}
              </a>
            </div>

            {/* Modification #2: Icônes devant chaque univers + #4: Phrases d'accroche + #5: Nouvel ordre */}
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              {universes.map((universe, index) => {
                const IconComponent = universe.icon;
                // Nom court pour mobile
                const shortName = universe.name.split(' - ')[0];
                const subName = universe.name.split(' - ')[1];
                return (
                  <Link 
                    key={index}
                    to={universe.link}
                    className="group bg-white/10 backdrop-blur-sm rounded-xl md:rounded-2xl p-4 md:p-6 border border-white/20 hover:bg-white/15 hover:border-green-500/50 transition-all"
                  >
                    {/* Modification #2: Icône devant chaque univers */}
                    <div className="flex items-start gap-2 md:gap-3 mb-2">
                      <div className="w-8 h-8 md:w-10 md:h-10 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <IconComponent size={18} className="text-green-400 md:w-5 md:h-5" />
                      </div>
                      <div className="text-sm md:text-lg font-bold text-white group-hover:text-green-400 transition-colors leading-tight">
                        <span className="block">{shortName}</span>
                        {subName && <span className="block text-xs md:text-sm font-normal text-slate-300">- {subName}</span>}
                      </div>
                    </div>
                    {/* Modification #4: Phrases d'accroche personnalisées */}
                    <div className="flex items-center gap-1 text-green-400 text-xs md:text-sm mt-2">
                      <span className="truncate">{universe.cta}</span>
                      <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform flex-shrink-0 md:w-3.5 md:h-3.5" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>

        {/* Search Modal */}
        <SearchBar isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2 text-white/50">
          <span className="text-sm">Découvrir</span>
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/50 rounded-full animate-bounce" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
